import {
  isoToDate,
  toISODate,
} from 'volto-ministerio-publico-federal/utils/brDate';

export const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

export const WEEKDAYS = [
  { short: 'D', long: 'domingo' },
  { short: 'S', long: 'segunda-feira' },
  { short: 'T', long: 'terça-feira' },
  { short: 'Q', long: 'quarta-feira' },
  { short: 'Q', long: 'quinta-feira' },
  { short: 'S', long: 'sexta-feira' },
  { short: 'S', long: 'sábado' },
];

export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const addMonths = (date: Date, months: number): Date => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate();

  return new Date(
    target.getFullYear(),
    target.getMonth(),
    Math.min(date.getDate(), lastDay),
  );
};

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Seis semanas fechadas a partir do domingo anterior ao dia 1º. O número de
 * células é fixo para a grade não mudar de altura ao trocar de mês.
 */
export const buildMonthGrid = (month: Date): Date[] => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = addDays(first, -first.getDay());

  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
};

export const DECADE = 10;

/**
 * Década a que o ano pertence. A grade mostra 12 anos — a década mais um de
 * folga em cada ponta —, então "2020 – 2029" continua legível como rótulo,
 * ao contrário de um bloco de 12 alinhado por múltiplos ("2016 – 2027").
 */
export const decadeStart = (year: number): number =>
  Math.floor(year / DECADE) * DECADE;

export const buildYearGrid = (year: number): number[] => {
  const start = decadeStart(year);

  return Array.from({ length: DECADE + 2 }, (_, index) => start - 1 + index);
};

/** Mantém o dia ao trocar de mês/ano, encurtando-o quando o mês é mais curto. */
export const setYearMonth = (date: Date, year: number, month: number): Date => {
  const lastDay = new Date(year, month + 1, 0).getDate();

  return new Date(year, month, Math.min(date.getDate(), lastDay));
};

export const isOutOfRange = (
  date: Date,
  min?: string,
  max?: string,
): boolean => {
  const iso = toISODate(date);
  const minDate = min ? isoToDate(min) : null;
  const maxDate = max ? isoToDate(max) : null;

  return Boolean(
    (minDate && iso < toISODate(minDate)) ||
      (maxDate && iso > toISODate(maxDate)),
  );
};

export const formatMonthLabel = (month: Date): string =>
  `${MONTHS[month.getMonth()]} de ${month.getFullYear()}`;

export const formatDayLabel = (date: Date): string =>
  `${date.getDate()} de ${MONTHS[date.getMonth()]} de ${date.getFullYear()}, ${
    WEEKDAYS[date.getDay()].long
  }`;

/** Um mês só é inalcançável quando nenhum dos seus dias cabe no intervalo. */
export const isMonthOutOfRange = (
  year: number,
  month: number,
  min?: string,
  max?: string,
): boolean => {
  const first = toISODate(new Date(year, month, 1));
  const last = toISODate(new Date(year, month + 1, 0));

  return Boolean((min && last < min) || (max && first > max));
};

export const isYearOutOfRange = (
  year: number,
  min?: string,
  max?: string,
): boolean => {
  const first = toISODate(new Date(year, 0, 1));
  const last = toISODate(new Date(year, 11, 31));

  return Boolean((min && last < min) || (max && first > max));
};
